export type SubscriptionTier = "free" | "pro" | "business" | "agency";

export type FeatureKey =
  | "ai_autofill"
  | "ask_ai"
  | "ai_review"
  | "pdf_export"
  | "detailed_scoring"
  | "unlimited_projects"
  | "generation_tools"
  | "teamProjects"
  | "teamInvite"
  | "stepAssignment"
  | "teamDashboard";

export const TIER_FEATURES: Record<SubscriptionTier, FeatureKey[]> = {
  free: [],
  pro: [
    "ai_autofill",
    "ask_ai",
    "ai_review",
    "pdf_export",
    "detailed_scoring",
    "unlimited_projects",
    "teamProjects", // viewer-only preview — can be invited to see team projects
  ],
  business: [
    "ai_autofill",
    "ask_ai",
    "ai_review",
    "pdf_export",
    "detailed_scoring",
    "unlimited_projects",
    "generation_tools",
  ],
  agency: [
    "ai_autofill",
    "ask_ai",
    "ai_review",
    "pdf_export",
    "detailed_scoring",
    "unlimited_projects",
    "generation_tools",
    "teamProjects",
    "teamInvite",
    "stepAssignment",
    "teamDashboard",
  ],
};

export const TIER_LIMITS: Record<SubscriptionTier, { maxProjects: number }> = {
  free: { maxProjects: 1 },
  pro: { maxProjects: Infinity },
  business: { maxProjects: Infinity },
  agency: { maxProjects: Infinity },
};

export const TIER_LABELS: Record<SubscriptionTier, string> = {
  free: "Free",
  pro: "Pro",
  business: "Business",
  agency: "Agency",
};

export const FEATURE_LABELS: Record<FeatureKey, string> = {
  ai_autofill: "AI Auto-Fill",
  ask_ai: "Ask AI Assistant",
  ai_review: "AI Review",
  pdf_export: "PDF Export",
  detailed_scoring: "Detailed Score Breakdown",
  unlimited_projects: "Unlimited Projects",
  generation_tools: "Generation Tools",
  teamProjects: "Team Projects (view shared projects)",
  teamInvite: "Team Invitations",
  stepAssignment: "Step Assignment",
  teamDashboard: "Team Dashboard",
};

/** Returns the minimum tier required for a given feature */
export function requiredTierForFeature(feature: FeatureKey): SubscriptionTier {
  if (TIER_FEATURES.free.includes(feature)) return "free";
  if (TIER_FEATURES.pro.includes(feature)) return "pro";
  if (TIER_FEATURES.business.includes(feature)) return "business";
  return "agency";
}

/** Stripe price IDs — replace placeholders with real IDs from your Stripe dashboard */
export const STRIPE_PRICE_IDS = {
  pro_monthly: "price_PRO_MONTHLY_PLACEHOLDER",
  pro_annual: "price_PRO_ANNUAL_PLACEHOLDER",
  business_monthly: "price_BUSINESS_MONTHLY_PLACEHOLDER",
  business_annual: "price_BUSINESS_ANNUAL_PLACEHOLDER",
  agency_monthly: "price_AGENCY_MONTHLY_PLACEHOLDER",
  agency_annual: "price_AGENCY_ANNUAL_PLACEHOLDER",
} as const;

export type BillingPeriod = "monthly" | "annual";

/** Pricing display values */
export const TIER_PRICING: Record<Exclude<SubscriptionTier, "free">, {
  monthly: string;
  annual: string;
  annualNote: string;
}> = {
  pro: { monthly: "$29", annual: "$228", annualNote: "or $228/year (save 34%)" },
  business: { monthly: "$99", annual: "$799", annualNote: "or $799/year (save 33%)" },
  agency: { monthly: "$349", annual: "$2,988", annualNote: "or $2,988/year (save 29%)" },
};
